import { useCallback, useMemo, useState, Fragment } from "react";
import { Button, Image } from "@storybook";
import { useSetRecoilState } from "recoil";

import {
  Card,
  Description,
  Divider,
  FormContainer,
  LabelDescription,
  SocialLoginOptions,
  SOCIAL_LOGIN_OPTIONS,
  CombinedInput,
} from "components";
import { SIGNUP_SCREEN, useSignUp } from "../../";
import { CurrentScreenState } from "global-states";
import { useInstance, useTranslation } from "hooks";
import { ROUTES } from "constants/routes";
import GlobalInstanceManager from "instance-manger";

import "./signup-form.scss";

/**
 * SignUpForm Component
 * Handles the sign-up form rendering, state, and navigation.
 */
export const SignUpForm = () => {
  const { t } = useTranslation(); // Hook for translations

  const [loading, setLoading] = useState(false); // State for loading indicator
  const setCurrentScreen = useSetRecoilState(CurrentScreenState); // Recoil state setter for current screen

  const { instanceDetails } = useInstance(); // Hook to get instance details
  const { onStepChangeEvent, webcomponentProps } = GlobalInstanceManager;
  const {
    form,
    handleChange,
    handleChangeCountry,
    submitCred,
    handleSocialSignUp,
    handleCombinedInputChange,
    setCurrentModal,
    loading: loader,
  } = useSignUp(); // Hook to manage sign-up form state and actions

  /**
   * Navigates to the sign-in page
   */
  const moveToSigninPage = () => {
    onStepChangeEvent({
      type: "change-page",
      pageTo: "signin",
      pageFrom: "singup",
      message: "user moved to signin page",
    });
    setCurrentScreen(ROUTES.SIGNIN);
  };

  /**
   * Determines if the continue button should be disabled
   */
  const isButtonDisabled = useMemo(() => {
    if (webcomponentProps.iscombinedinput) {
      const { isError } =
        form.find((el) => instanceDetails.signUp?.scope.includes(el.name)) ??
        {};
      return isError;
    }

    const { isError, value } =
      form.find((el) => instanceDetails.signUp?.scope.includes(el.name)) ?? {};
    return isError || !value;
  }, [form, instanceDetails, webcomponentProps.iscombinedinput]);

  const { logo, signUp, providers, properties } = useMemo(
    () => instanceDetails,
    [instanceDetails]
  );

  /**
   * Handles the continue button click
   */
  const handleOnClickContinue = useCallback(async () => {
    setLoading(true);
    await submitCred(
      () => {
        setCurrentModal(SIGNUP_SCREEN.VERIFY);
      },
      () => setLoading(false)
    );

    setLoading(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitCred]);

  const { fields } = useMemo(() => {
    return {
      fields: form.map((el) =>
        !signUp?.scope.includes(el.name)
          ? { ...el, isHidden: true, disabled: false } // Hide form elements not in the sign-up scope
          : { ...el, disabled: false }
      ),
    };
  }, [form, signUp?.scope]);

  const alreadyAccount = (
    <div className="sublabel signup-wrapper__footer">
      {" "}
      {/* Footer section */}
      {!webcomponentProps.hide_navigation && (
        <Description
          content={
            <div onClick={moveToSigninPage}>
              {t("signup.already_account")} {/* Translated text */}
              <span>{t("signup.login")}</span>{" "}
              {/* Clickable text to navigate to sign-in */}
            </div>
          }
          className="text-center signup-footer" // Center aligned text
        />
      )}
    </div>
  );

  const ssoElemet = (
    <SocialLoginOptions
      options={SOCIAL_LOGIN_OPTIONS.filter(
        (el) =>
          providers?.map((elm) => elm.key)?.includes(el.key) ||
          (el.key === "simplici" && properties)
      )}
      isShowShowMoreSSO={false}
      // eslint-disable-next-line no-console
      handleSelect={handleSocialSignUp}
    />
  );

  if (signUp?.scope.length === 0 && providers?.length) {
    return (
      <Card
        className="flex direction-col gap-16 border-0"
        isLoading={loading || loader}
      >
        <>
          {ssoElemet}
          {alreadyAccount}
        </>
      </Card>
    );
  }
  const { iscombinedinput } = webcomponentProps;

  return (
    <Card
      className="flex direction-col gap-24 sm-border-0"
      isLoading={loading || loader}
    >
      {/* Main container for the form */}
      <Fragment>
        {logo && (
          <Image
            className="object-fit-contain h-48 w-fit-content w-170 "
            url={logo}
          />
        )}
        {/* Wrapper for multiple child elements */}
        <LabelDescription
          label={t("signup.label")} // Translated label text
          description={t("signup.sublabel")} // Translated sublabel text
        />
        {iscombinedinput ? (
          <CombinedInput handleChange={handleCombinedInputChange} />
        ) : (
          <FormContainer
            form={fields}
            handleChange={(e: any) => handleChange(e)} // Handler for form input changes
            handleChangeCountry={handleChangeCountry} // Handler for country input changes
          />
        )}
        <Button
          label={t("signup.continue")} // Translated button label
          type="button__filled button__filled--primary button__block" // Button type and styling
          handleClick={handleOnClickContinue} // Click handler for the continue button
          disabled={isButtonDisabled || loading} // Disable button based on conditions
          loader={loading} // Loading state for the button
        />
        <Divider label={t("or")} /> {/* Divider with translated "or" text */}
        {ssoElemet}
        <Description className="text-center" content={t("signup.footer")} />
        {!iscombinedinput && alreadyAccount}
      </Fragment>
    </Card>
  );
};
